import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  remoteTotalSelector,
} from 'state/slices/orderItem';
import { usePagination } from 'hooks/pagination';
import {
  OrderItem,
  Organization,
  Product,
  ProductionItem,
  ProductionItemDetail,
  SeedingBatch,
  Tray,
} from 'constants/interface';
import { Search, Table, Tag } from 'UI';
import {
  ProductionItemStatusEnum,
  SeedingBatchStatusDisplayMapping,
} from 'constants/order';
import {
  cleanProductionItemsAction,
  getProductionItemListAction,
  isLoadingProductionItemsSelector,
  productionItemsEntitiesSelector,
  productionItemsIdsSelector,
} from 'state/slices/productionItem';
import { GetProductionItemList } from 'api/productionItem';
import {
  buildProductionItemQuantityDisplay,
  buildRegularProductDisplay,
  getSeedingStatus,
} from 'helpers/order';
import { buildTrayDisplay } from 'helpers/tray';
import { ProductTypeMapping } from 'constants/product';
import { formatDate, parseUtcStringDate } from 'helpers/utils';

import TableRowAction from './components/TableRowAction';

import {
  SearchContainer,
} from './style';

interface SeedingProductionItemsTabProps {
  selectedOrg: Organization
}

const SeedingProductionItemsTab = ({
  selectedOrg,
}: SeedingProductionItemsTabProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const productionItemsEntities = useSelector(productionItemsEntitiesSelector);
  const productionItemsIds = useSelector(productionItemsIdsSelector);
  const isRemoteLoading = useSelector(isLoadingProductionItemsSelector);

  // const orderItems = productionItemsIds.map((orderItemId) => {
  //   const entity = productionItemsEntities[orderItemId];
  //   if (!entity) return undefined;
  //   const tableProductionItem: OrderItem & {
  //     key?: string,
  //   } = {
  //     ...entity,
  //     key: entity.id,
  //   };
  //   return tableOrderItem;
  // }).filter((tableOrderItem) => !!tableOrderItem) as (OrderItem & { key: string})[];
  const productionItems = productionItemsIds
    .map((productionItemId) => productionItemsEntities[productionItemId])
    .filter((prodItem): prodItem is ProductionItem => !!prodItem);

  // const [currentActiveAction, setCurrentActiveAction] = useState<number>();

  const remoteFetch = (
    paginationData: GetProductionItemList,
  ) => {
    dispatch(getProductionItemListAction({
      ...paginationData,
    }));
  };
  const pagination = usePagination<ProductionItem>(
    remoteFetch,
    {
      filter: {
        status: ProductionItemStatusEnum.SEEDING,
        organization: selectedOrg.id,
      },
      order: 'descend',
    },
  );

  useEffect(() => {
    dispatch(cleanProductionItemsAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  // const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  // const orderTypeFilter = pageData.filter.type ?? null;
  // const orderStatusFilter = pageData.filter.status ?? null;

  // const statusFilterList = [
  //   ...Object.values(OrderProposalStatusEnum),
  //   ...Object.values(OrderProductionStatusEnum),
  //   ...Object.values(OrderOtherStatusEnum),
  // ];

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar itens" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table<ProductionItem>
        rowKey="id"
        dataSource={productionItems}
        pagination={pageData}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Cliente"
          key="client"
          dataIndex="orderItem"
          render={(
            orderItem: OrderItem,
          ) => orderItem.order.client.name}
        />
        {/* <Table.Column
          title="Localização"
          key="location"
          dataIndex="orderItem"
          render={(
            orderItem: OrderItem,
          ) => {
            const { location, client } = orderItem.order;
            const display = location
              ? `${location.name} - ${location.city}/${location.state}`
              : `${client.city}/${client.state}`;
            return display;
          }}
        /> */}
        <Table.Column
          title="Data semeio prev"
          key="plannedSeedingDate"
          dataIndex="orderItem"
          render={(
            orderItem: OrderItem,
          ) => {
            const display = formatDate(parseUtcStringDate(orderItem.plannedSeedDate));
            return display;
          }}
        />
        <Table.Column
          title="Produto"
          key="product"
          dataIndex="product"
          render={(
            product: Product,
            productionItem: ProductionItem,
          ) => {
            const display = buildRegularProductDisplay(productionItem, {
              quantity: false,
              tray: false,
            });
            return display;
          }}
        />
        <Table.Column
          title="Bdj"
          key="tray"
          dataIndex="tray"
          render={(
            tray: Tray,
          ) => {
            const display = buildTrayDisplay(tray);
            return display;
          }}
        />
        <Table.Column
          title="Formação"
          key="type"
          dataIndex="product"
          // filters={Object.values(OrderTypeEnum).map((orderType) => ({
          //   text: OrderTypeDisplayMapping[orderType].name,
          //   value: orderType,
          // }))}
          // filterMultiple={false}
          // filteredValue={orderTypeFilter !== null ? [orderTypeFilter] : null}
          render={(product: Product) => {
            const displayName = ProductTypeMapping[product.type];
            return displayName;
          }}
        />
        <Table.Column
          title="Data semeio prog"
          key="seedingDate"
          dataIndex="seedingBatches"
          // filters={Object.values(OrderTypeEnum).map((orderType) => ({
          //   text: OrderTypeDisplayMapping[orderType].name,
          //   value: orderType,
          // }))}
          // filterMultiple={false}
          // filteredValue={orderTypeFilter !== null ? [orderTypeFilter] : null}
          render={(seedingBatches: SeedingBatch[]) => {
            const mainSeedingBatch = seedingBatches ? seedingBatches[0] : undefined;
            const display = mainSeedingBatch?.seedingDate
              // ? dayjs(mainSeedingBatch.seedingDate).toDate().toLocaleDateString('pt-br')
              ? formatDate(parseUtcStringDate(mainSeedingBatch.seedingDate))
              : '-';
            return display;
          }}
        />
        <Table.Column
          title="Quantidade programada"
          key="calculatedQuantity"
          dataIndex="calculatedQuantity"
          // filters={Object.values(OrderTypeEnum).map((orderType) => ({
          //   text: OrderTypeDisplayMapping[orderType].name,
          //   value: orderType,
          // }))}
          // filterMultiple={false}
          // filteredValue={orderTypeFilter !== null ? [orderTypeFilter] : null}
          render={(calculatedQuantity: number, record: ProductionItemDetail) => {
            // const display = buildSimpleQuantityDisplay(calculatedQuantity);
            const display = buildProductionItemQuantityDisplay(record);
            return display;
          }}
        />
        <Table.Column
          title="Estufa prog"
          key="greenhouse"
          dataIndex="seedingBatches"
          // filters={Object.values(OrderTypeEnum).map((orderType) => ({
          //   text: OrderTypeDisplayMapping[orderType].name,
          //   value: orderType,
          // }))}
          // filterMultiple={false}
          // filteredValue={orderTypeFilter !== null ? [orderTypeFilter] : null}
          render={(seedingBatches: SeedingBatch[]) => {
            const mainSeedingBatch = seedingBatches ? seedingBatches[0] : undefined;
            const display = mainSeedingBatch?.greenhouse?.name || '-';
            return display;
          }}
        />
        <Table.Column
          title="Status"
          key="status"
          dataIndex="seedingBatches"
          // filters={statusFilterList.map((statusType) => ({
          //   text: OrderStatusDisplayMapping[statusType].name,
          //   value: statusType,
          // }))}
          // filterMultiple={false}
          // filteredValue={orderStatusFilter !== null ? [orderStatusFilter] : null}
          render={(seedingBatches: SeedingBatch[], item: ProductionItem) => {
            // const mainSeedingBatch = seedingBatches ? seedingBatches[0] : undefined;
            const status = getSeedingStatus(item);
            const { name: displayName, color } = SeedingBatchStatusDisplayMapping[status];
            return (
              <Tag color={color}>
                {displayName}
              </Tag>
            );
          }}
        />
        <Table.Column
          title=""
          key="action"
          dataIndex="id"
          render={(id: string, record: ProductionItem) => {
            const actionButton = (
              <TableRowAction
                item={record}
                // index={index}
                // onVisibleChange={(visibleIndex) => setCurrentActiveAction(visibleIndex)}
              />
            );
            return actionButton;
          }}
        />
      </Table>
    </>
  );
};

export default SeedingProductionItemsTab;
